import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../config";
import { SPECIALTIES } from "../../SpecialtyContext";

const EMPTY = {
  title: "", text: "", image_url: "", link_url: "", button_label: "",
  theme: "teal", specialty: "", starts_at: "", ends_at: "", position: 0, is_active: true,
};

// В базе — naive UTC; в поле datetime-local — местное время администратора.
const toLocalInput = s => {
  if (!s) return "";
  const d = new Date(s.endsWith("Z") ? s : `${s}Z`);
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toUtcNaive = v => (v ? new Date(v).toISOString().slice(0, 19) : null);

function statusOf(b) {
  if (!b.is_active) return "выключен";
  const now = Date.now();
  if (b.starts_at && new Date(`${b.starts_at}Z`).getTime() > now) return "ждёт начала";
  if (b.ends_at && new Date(`${b.ends_at}Z`).getTime() < now) return "завершён";
  return "показывается";
}

export default function BannersTab({ token }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    fetch(`${API_URL}/admin/banners`, { headers: auth })
      .then(r => (r.ok ? r.json() : []))
      .then(setItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const edit = b => {
    setEditingId(b.id);
    setError("");
    setForm({
      title: b.title, text: b.text || "", image_url: b.image_url || "", link_url: b.link_url || "",
      button_label: b.button_label || "", theme: b.theme, specialty: b.specialty || "",
      starts_at: toLocalInput(b.starts_at), ends_at: toLocalInput(b.ends_at), position: b.position,
      is_active: b.is_active,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const reset = () => { setEditingId(null); setForm(EMPTY); setError(""); };

  const upload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`${API_URL}/upload/image`, { method: "POST", headers: auth, body: fd });
    const data = await r.json().catch(() => ({}));
    if (r.ok && data.url) set("image_url", data.url);
    else setError(data.error || "Не удалось загрузить картинку");
  };

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const body = {
      title: form.title.trim(), text: form.text.trim() || null,
      image_url: form.image_url.trim() || null, link_url: form.link_url.trim() || null,
      button_label: form.button_label.trim() || null, theme: form.theme, specialty: form.specialty || null,
      starts_at: toUtcNaive(form.starts_at), ends_at: toUtcNaive(form.ends_at),
      position: Number(form.position) || 0, is_active: form.is_active,
    };
    try {
      const r = await fetch(`${API_URL}/admin/banners${editingId ? `/${editingId}` : ""}`, {
        method: editingId ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", ...auth },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map(d => d.msg).join("; ") : data.detail;
        throw new Error(detail || "Не удалось сохранить");
      }
      reset();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = b =>
    fetch(`${API_URL}/admin/banners/${b.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ is_active: !b.is_active }),
    }).then(load);

  const remove = b => {
    if (!window.confirm(`Удалить слайд «${b.title}»? Счётчики будут потеряны.`)) return;
    fetch(`${API_URL}/admin/banners/${b.id}`, { method: "DELETE", headers: auth }).then(load);
  };

  return (
    <div>
      <p style={{ color: "#888", fontSize: "0.9rem", marginTop: 0 }}>
        Слайды главной страницы: анонсы мероприятий и новости портала. Первым всегда идёт приветствие, затем — слайды
        ниже по порядку. Портал не размещает рекламу.
      </p>

      <form onSubmit={save} className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>{editingId ? "Редактирование слайда" : "Новый слайд"}</h3>
        <div className="admin-form-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>Цвет
            <select className="admin-input" value={form.theme} onChange={e => set("theme", e.target.value)}>
              <option value="teal">Бирюзовый</option>
              <option value="magenta">Малиновый</option>
              <option value="gray">Серо-голубой</option>
            </select>
          </label>
        </div>
        <label>Заголовок *
          <input className="admin-input" value={form.title} maxLength={200} required onChange={e => set("title", e.target.value)} />
        </label>
        <label>Текст
          <textarea className="admin-textarea" rows={3} value={form.text} onChange={e => set("text", e.target.value)} />
        </label>
        <div className="admin-form-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>Ссылка (https://… или /articles)
            <input className="admin-input" value={form.link_url} onChange={e => set("link_url", e.target.value)} />
          </label>
          <label>Надпись на кнопке
            <input className="admin-input" value={form.button_label} maxLength={60} placeholder="Подробнее" onChange={e => set("button_label", e.target.value)} />
          </label>
        </div>
        <label>Картинка (до 5 МБ)
          <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={upload} />
        </label>
        {form.image_url && (
          <div style={{ margin: "8px 0" }}>
            <img src={form.image_url} alt="" style={{ maxWidth: 220, maxHeight: 120, borderRadius: 8, display: "block" }} />
            <button type="button" onClick={() => set("image_url", "")} style={{ marginTop: 6, background: "none", color: "#c0392b", padding: 0 }}>убрать картинку</button>
          </div>
        )}
        <div className="admin-form-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>Показывать с
            <input type="datetime-local" className="admin-input" value={form.starts_at} onChange={e => set("starts_at", e.target.value)} />
          </label>
          <label>Показывать до
            <input type="datetime-local" className="admin-input" value={form.ends_at} onChange={e => set("ends_at", e.target.value)} />
          </label>
          <label>Специальность
            <select className="admin-input" value={form.specialty} onChange={e => set("specialty", e.target.value)}>
              <option value="">Везде (общий слайд)</option>
              {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label>Порядок (меньше — раньше)
            <input type="number" className="admin-input" value={form.position} onChange={e => set("position", e.target.value)} />
          </label>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0" }}>
          <input type="checkbox" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} /> Показывать на сайте
        </label>
        {error && <div style={{ color: "#c0392b", marginBottom: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" disabled={busy}>{editingId ? "Сохранить" : "Добавить слайд"}</button>
          {editingId && <button type="button" onClick={reset} style={{ background: "var(--gray-300)", color: "var(--gray-800)" }}>Отмена</button>}
        </div>
      </form>

      {items === null ? <p>Загрузка…</p> : items.length === 0 ? (
        <p style={{ color: "#888" }}>Слайдов пока нет — на главной показывается только приветствие.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Слайд</th><th>Статус</th><th>Показы</th><th>Клики</th><th>CTR</th><th></th></tr>
            </thead>
            <tbody>
              {items.map(b => (
                <tr key={b.id}>
                  <td>
                    <strong>{b.title}</strong>
                    <div style={{ color: "#888", fontSize: "0.8rem" }}>
                      порядок {b.position}{b.specialty ? ` · ${SPECIALTIES.find(s => s.id === b.specialty)?.label || b.specialty}` : ""}
                    </div>
                  </td>
                  <td>{statusOf(b)}</td>
                  <td>{b.views}</td>
                  <td>{b.clicks}</td>
                  <td>{b.views ? `${((b.clicks / b.views) * 100).toFixed(1)}%` : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button onClick={() => edit(b)} style={{ marginRight: 6, padding: "4px 10px" }}>Изменить</button>
                    <button onClick={() => toggle(b)} style={{ marginRight: 6, padding: "4px 10px", background: "var(--gray-500)" }}>{b.is_active ? "Выключить" : "Включить"}</button>
                    <button onClick={() => remove(b)} style={{ padding: "4px 10px", background: "#c0392b" }}>Удалить</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
